import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { useI18n } from "@/i18n/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];
const LANGS = ["fr", "es", "en"];

export default function MaintenancePage({ site }) {
  const { lang, setLang, t } = useI18n();
  const m = t.maintenance;

  return (
    <div className="relative flex min-h-screen flex-col bg-pine text-cream" data-testid="maintenance-page">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(176,104,44,0.28),transparent_55%)]" />
      <header className="relative z-10 flex items-center justify-between px-6 py-6 sm:px-12">
        <div className="flex items-center gap-3">
          <Logo className="h-12 w-12" />
          <span className="font-serif text-xl italic">Monte Yolli</span>
        </div>
        <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em]">
          {LANGS.map((l) => (
            <button
              key={l}
              type="button"
              data-testid={`maintenance-lang-${l}`}
              onClick={() => setLang(l)}
              className={`px-2 py-1 transition-colors duration-300 ${lang === l ? "text-copper" : "text-cream/50 hover:text-cream"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </header>

      <main className="relative z-10 flex flex-1 flex-col justify-center px-6 pb-24 sm:px-12 lg:px-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-copper"
        >
          {m.badge}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: EASE }}
          className="max-w-4xl font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl"
          data-testid="maintenance-title"
        >
          {m.title} <em className="text-copper">{m.titleEm}</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-cream/75"
        >
          {m.text}
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.45 }}
          className="mt-12 flex flex-col gap-4 text-sm sm:flex-row sm:gap-10"
        >
          <a href={`mailto:${site.contact_email}`} className="inline-flex items-center gap-3 text-cream/80 transition-colors duration-300 hover:text-copper" data-testid="maintenance-email">
            <Mail className="h-4 w-4 text-copper" /> {site.contact_email}
          </a>
          <a href={`tel:${site.contact_phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-3 text-cream/80 transition-colors duration-300 hover:text-copper" data-testid="maintenance-phone">
            <Phone className="h-4 w-4 text-copper" /> {site.contact_phone}
          </a>
        </motion.div>
      </main>

      <footer className="relative z-10 flex items-center justify-between px-6 py-6 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40 sm:px-12">
        <span>Tulancingo de Bravo · Hidalgo · México</span>
        <Link to="/admin" className="transition-colors duration-300 hover:text-copper" data-testid="maintenance-admin-link">
          Admin
        </Link>
      </footer>
    </div>
  );
}
